// src/pages/festejos/FestojoPublicPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Link,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  LocationOn,
  Email,
  Phone,
  CalendarToday,
  Church,
  Share,
  Close,
  PlayArrow,
  OpenInNew,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import festojoService from '../../services/festojoService';
import { Festejo, Section, ProgramacaoEvent } from '../../types/festejo';

const FestojoPublicPage: React.FC = () => {
  const { festejoSlug } = useParams<{ festejoSlug: string }>();
  const navigate = useNavigate();
  
  const [festejo, setFestejo] = useState<Festejo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    const loadFestejo = async () => {
      if (!festejoSlug) {
        setError('Slug do festejo não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await festojoService.getBySlug(festejoSlug);
        
        if (data.status !== 'published') {
          setError('Este festejo não está disponível publicamente');
          setLoading(false);
          return;
        }
        
        setFestejo(data);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar festejo');
      } finally {
        setLoading(false);
      }
    };

    loadFestejo();
  }, [festejoSlug]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: festejo?.title,
          text: festejo?.description,
          url: window.location.href,
        });
      } catch (err) {
        // Fallback para copiar URL
        navigator.clipboard.writeText(window.location.href);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const formatDate = (dateString: string) => {
    return format(parseISO(dateString), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  const formatDateTime = (dateString: string) => {
    return format(parseISO(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const renderSection = (section: Section) => {
    switch (section.type) {
      case 'texto':
        return (
          <Paper key={section.id} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              {section.title}
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
              {section.content}
            </Typography>
          </Paper>
        );

      case 'imagem':
        return (
          <Paper key={section.id} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              {section.title}
            </Typography>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img
                src={section.imageUrl}
                alt={section.title}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedImage(section.imageUrl!)}
              />
            </Box>
            {section.description && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {section.description}
              </Typography>
            )}
          </Paper>
        );

      case 'galeria':
        return (
          <Paper key={section.id} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              {section.title}
            </Typography>
            <ImageList variant="masonry" cols={3} gap={8}>
              {section.images?.map((image) => (
                <ImageListItem key={image.id}>
                  <img
                    src={image.url}
                    alt={image.caption || section.title}
                    loading="lazy"
                    style={{ cursor: 'pointer', borderRadius: 8 }}
                    onClick={() => setSelectedImage(image.url)}
                  />
                  {image.caption && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      {image.caption}
                    </Typography>
                  )}
                </ImageListItem>
              ))}
            </ImageList>
          </Paper>
        );

      case 'video':
        return (
          <Paper key={section.id} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              {section.title}
            </Typography>
            <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, mb: 2 }}>
              <iframe
                src={section.videoUrl}
                title={section.title}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: 8
                }}
                allowFullScreen
              />
            </Box>
            {section.description && (
              <Typography variant="body2" color="text.secondary">
                {section.description}
              </Typography>
            )}
          </Paper>
        );

      case 'link_externo':
        return (
          <Paper key={section.id} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              {section.title}
            </Typography>
            {section.description && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {section.description}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              href={section.url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNew />}
            >
              Acessar Link
            </Button>
          </Paper>
        );

      case 'programacao':
        return (
          <Paper key={section.id} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom color="primary">
              {section.title}
            </Typography>
            <Grid container spacing={2}>
              {section.events?.map((event: ProgramacaoEvent) => (
                <Grid item xs={12} md={6} key={event.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {event.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarToday sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">
                          {formatDateTime(event.dateTime)}
                        </Typography>
                      </Box>
                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <LocationOn sx={{ mr: 1, fontSize: 16 }} />
                          <Typography variant="body2">
                            {event.location}
                          </Typography>
                        </Box>
                      )}
                      {event.description && (
                        <Typography variant="body2" color="text.secondary">
                          {event.description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando festejo...
        </Typography>
      </Container>
    );
  }

  if (error || !festejo) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Festejo não encontrado'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          Voltar ao Início
        </Button>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: festejo.theme.primaryColor + '10',
      }}
    >
      {/* Header do Festejo */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${festejo.theme.primaryColor}, ${festejo.theme.secondaryColor})`,
          color: 'white',
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{ fontFamily: festejo.theme.fontFamily, fontWeight: 'bold' }}
              >
                {festejo.title}
              </Typography>
              <Typography variant="h5" sx={{ mb: 2, opacity: 0.9 }}>
                Em honra a {festejo.padroeiro}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                {festejo.description}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Chip
                  icon={<CalendarToday />}
                  label={`${formatDate(festejo.startDate)} - ${formatDate(festejo.endDate)}`}
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <Chip
                  icon={<Church />}
                  label={festejo.paroquia}
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              {festejo.coverImage && (
                <Box sx={{ textAlign: 'center' }}>
                  <img
                    src={festejo.coverImage}
                    alt={festejo.title}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 16,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                    }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Informações de Contato */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Informações de Contato
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {festejo.endereco}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {festejo.cidade}, {festejo.estado}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Email sx={{ mr: 2, color: 'primary.main' }} />
                <Link href={`mailto:${festejo.contactEmail}`} underline="hover">
                  {festejo.contactEmail}
                </Link>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 2, color: 'primary.main' }} />
                <Link href={`tel:${festejo.contactPhone}`} underline="hover">
                  {festejo.contactPhone}
                </Link>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Share />}
                  onClick={handleShare}
                >
                  Compartilhar
                </Button>
                
                {festejo.mapLink && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<LocationOn />}
                    href={festejo.mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Ver no Mapa
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Seções do Festejo */}
        {festejo.sections
          .sort((a, b) => a.order - b.order)
          .map(renderSection)}
      </Container>

      {/* Modal de Imagem */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              }
            }}
          >
            <Close />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Imagem ampliada"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FestojoPublicPage;

